import { Alert, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import ItemBuilder from '../../features/item-builder/ItemBuilder';
import useAIMessaging from '../../features/messaging/useAIMessaging.hooks';
import useHypothesisFrames from '../../hooks/useHypothesisFrames.hooks';
import useAgentCartSync from '../../hooks/useAgentCartSync.hooks';
import useExpressTimer from '../../hooks/useExpressTimer.hooks';
import useUnresolvedItemsSummary from '../../hooks/useUnresolvedItemsSummary.hooks';
import useSessionStatus from '../../hooks/sessionStatus/useSessionStatus.hooks';
import useWebSocketAlert from '../../hooks/useWebSocketAlert.hooks';
import {
  considerTimePeriods,
  considerTimePeriodsForUnifiedMenu,
} from '../../reducers/menuSlice';
import { selectCartModality } from '../../selectors/cart';
import {
  selectActiveRestaurantTimezone,
  selectedStageSelector,
} from '../../selectors/restaurant';
import { ICartItemRefs, SetCartItemRef } from '../../types';
import Colors from '../../utils/color';
import { TIME_PERIOD_CHECK_INTERVAL } from '../../utils/constants';
import { getMenuUnificationFeatureFlag } from '../../utils/session-storage';
import { selectFeatureFlagFinalOrderValidation } from '../../redux/features/config/config.selector';
import { useTaskLoadingMetricsContext } from '../../hooks/taskLoadingMetrics/TaskLoadingMetricsContext';
import AIStatusBar from '../AIStatusBar';
import AISuggestions from '../AISuggestions';
import FinalOrderBackdrop from '../FinalOrderBackdrop';
import TaskLoadingMetricsDisplay from '../TaskLoadingMetricsDisplay';
import AgentCartCard from './AgentCartCard';
import BottomActionBar from './BottomActionBar';
import BannerCard from './BannerCard';
import LiveTranscriptCard from './LiveTranscriptCard';
import ReminderCard from './ReminderCard';
import useTranscriptExpand from './useTranscriptExpand.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  page: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.porcelain,
  },
  topRow: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
    gap: spacing(1),
    // No top/bottom padding: cards attach to the header above and the bottom
    // bar below. Horizontal padding keeps them off the screen edges.
    padding: `0 ${spacing(1)}`,
    // Positioning context for the Live Transcript takeover overlay (it expands
    // out of the left pane to cover left + center on AI-supervised takeover).
    position: 'relative',
  },
  leftPane: {
    width: 360,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Left card gets top space; only the center attaches to the header.
    paddingTop: spacing(1),
  },
  // Single left card: transcript section + reminder section + AI status bar,
  // each an internal white card under its heading (mirrors the mockup).
  leftCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1.5),
    backgroundColor: '#F9FAFB',
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: spacing(1),
    padding: spacing(1.5),
  },
  // AI-supervised takeover: the whole left card lifts out and overlays the
  // left + center area (stops before the right Agent Cart), so the transcript
  // gets the room while the reminder + AI status sections stay visible.
  leftCardExpanded: {
    position: 'absolute',
    top: spacing(1),
    left: spacing(1),
    // Right cart pane (360) + the gap before it + the row's right padding.
    right: `calc(360px + ${spacing(2)})`,
    bottom: 0,
    zIndex: 5,
  },
  // AI status bar slot. flexShrink:0 stops the flex transcript above from
  // squeezing it. Negative margins cancel the left card's padding so the bar
  // attaches flush to the card's bottom corners. All AIStatusBar overrides are
  // scoped here so the shared component (and its tests) stay untouched.
  statusSlot: {
    flexShrink: 0,
    margin: spacing(0, -1.5, -1.5),
    '& [data-testid="ai-status-bar"]': {
      // Size to content up to ~2 lines, then scroll (was a fixed 48px).
      height: 'auto',
      maxHeight: spacing(7),
      overflowY: 'auto',
      padding: spacing(1),
      borderBottomLeftRadius: spacing(1),
      borderBottomRightRadius: spacing(1),
    },
    '& [data-testid="ai-status-bar"] > div': {
      // Match the transcript text size (0.75rem / 12px) so the left pane is
      // consistent (the shared component's own style sets 1rem / 30px).
      height: 'auto',
      fontSize: '0.75rem',
      lineHeight: 1.4,
      // The shared component lays the label + message out as a flex row, which
      // pins "AUTO AI:" in its own left column and confines the message to a
      // narrow column on the right — wasting the space below the short label in
      // this 360px pane. Flow them as inline text instead so the message wraps
      // across the full bar width (lines after the first flow back under the
      // label).
      display: 'block',
    },
    '& [data-testid="ai-status-bar"] > div > div': {
      display: 'inline',
    },
  },
  middlePane: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Anchor for the absolutely-positioned WebSocket connection alerts.
    position: 'relative',
  },
  alertClass: {
    position: 'absolute',
    left: spacing(2),
    right: spacing(2),
    zIndex: 200,
    alignItems: 'center',
  },
  // Single center card: Current Step section pinned at the top, option groups
  // scrolling below it.
  centerCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.white,
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: spacing(1),
    overflow: 'hidden',
  },
  optionScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: spacing(0, 1.5, 1.5, 1.5),
    // ItemBuilder keeps its original spacing(7) top padding (it makes room for
    // the Banner overlay in the OLD layout). Here BannerCard is a separate flex
    // child above, so drop that top gap to keep the option groups flush.
    '& #itemBuilder': {
      paddingTop: 0,
    },
  },
  rightPane: {
    width: 360,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    paddingTop: spacing(1),
  },
}));

/**
 * MainPageLayout — new HITL terminal layout (replacement for MainContent).
 *
 * Card containers are laid out; real content gets wired into each card one at
 * a time. The top app bar (Header) lives outside this component (App.tsx).
 */
const MainPageLayout = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const cartItemRefs = useRef<ICartItemRefs>({});
  const setCartItemRef: SetCartItemRef = (cartItemId, ref) => {
    cartItemRefs.current[cartItemId] = ref;
  };

  // AI auto-mode status bar visibility.
  const { isAIActive, isAIAutoMode } = useAIMessaging();
  const showAIStatus = isAIActive && isAIAutoMode;

  // Left-card takeover expansion (transcript + reminder + AI status grow as one).
  const { isExpanded, setIsExpanded } = useTranscriptExpand();

  // Task-loading metrics readout.
  const { lastSentMetrics } = useTaskLoadingMetricsContext();

  // Time-period menu rollover inputs.
  const timezone = useAppSelector(selectActiveRestaurantTimezone);
  const stage = useAppSelector(selectedStageSelector);
  const modality = useAppSelector(selectCartModality);

  // WebSocket connection alert state.
  const eventWSConnectionMessage = useAppSelector(
    (state) => state.messages.eventWSConnectionMessage
  );
  const audioWSConnectionMessage = useAppSelector(
    (state) => state.messages.audioWSConnectionMessage
  );
  const eventWSConnectionSuccess = useAppSelector(
    (state) => state.messages.eventWSConnectionSuccess
  );
  const audioWSConnectionSuccess = useAppSelector(
    (state) => state.messages.audioWSConnectionSuccess
  );

  const FEATURE_FLAG_FINAL_ORDER_VALIDATION = useAppSelector(
    selectFeatureFlagFinalOrderValidation
  );

  const { showAlert: showEventAlert, handleCloseAlert: handleCloseEventAlert } =
    useWebSocketAlert({
      wsConnectionMessage: eventWSConnectionMessage,
      wsConnectionSuccess: eventWSConnectionSuccess,
    });

  const { showAlert: showAudioAlert, handleCloseAlert: handleCloseAudioAlert } =
    useWebSocketAlert({
      wsConnectionMessage: audioWSConnectionMessage,
      wsConnectionSuccess: audioWSConnectionSuccess,
    });

  // Poll the clock to roll the menu over between day-parts (breakfast/lunch/…).
  useEffect(() => {
    if (timezone) {
      const timer = setInterval(() => {
        if (getMenuUnificationFeatureFlag()) {
          dispatch(considerTimePeriodsForUnifiedMenu({ timezone }));
        } else {
          dispatch(considerTimePeriods({ modality, timezone, stage }));
        }
      }, TIME_PERIOD_CHECK_INTERVAL);
      return () => {
        clearInterval(timer);
      };
    }
  }, [timezone, dispatch, modality, stage]);

  // Core call-handling side effects (cart rebuild from hypotheses, unresolved
  // items summary, session lifecycle). These run for their effects only.
  useHypothesisFrames();
  useUnresolvedItemsSummary();
  useSessionStatus();
  useAgentCartSync();
  useExpressTimer();

  return (
    <div id="main-page-layout" className={classes.page}>
      <div className={classes.topRow}>
        {/* LEFT SIDEBAR: one card — transcript + reminder sections + AI status. */}
        <div className={classes.leftPane}>
          <div
            className={`${classes.leftCard} ${
              isExpanded ? classes.leftCardExpanded : ''
            }`}
          >
            <LiveTranscriptCard
              isExpanded={isExpanded}
              onCollapse={() => setIsExpanded(false)}
            />
            <AISuggestions />
            <ReminderCard />
            {showAIStatus && (
              <div className={classes.statusSlot}>
                <AIStatusBar />
              </div>
            )}
          </div>
        </div>

        {/* MIDDLE: one card — current step (top) + option groups (ItemBuilder) */}
        <div className={classes.middlePane}>
          <TaskLoadingMetricsDisplay metrics={lastSentMetrics} />
          {showEventAlert && eventWSConnectionMessage && (
            <Alert
              variant="filled"
              severity={eventWSConnectionSuccess ? 'success' : 'error'}
              className={classes.alertClass}
              onClose={
                eventWSConnectionSuccess ? handleCloseEventAlert : undefined
              }
            >
              Event WebSocket: {eventWSConnectionMessage}
            </Alert>
          )}
          {showAudioAlert && audioWSConnectionMessage && (
            <Alert
              variant="filled"
              severity={audioWSConnectionSuccess ? 'success' : 'error'}
              className={classes.alertClass}
              onClose={
                audioWSConnectionSuccess ? handleCloseAudioAlert : undefined
              }
              sx={{
                top:
                  showEventAlert && eventWSConnectionMessage
                    ? '72px'
                    : undefined,
              }}
            >
              Audio WebSocket: {audioWSConnectionMessage}
            </Alert>
          )}
          <div className={classes.centerCard}>
            <BannerCard />
            <div className={classes.optionScroll}>
              <ItemBuilder setCartItemRef={setCartItemRef} />
            </div>
          </div>
        </div>

        {/* RIGHT: agent cart + totals + order actions */}
        <div className={classes.rightPane}>
          <AgentCartCard />
        </div>
      </div>

      {/* BOTTOM: quick response + conversation + order flow + escalation */}
      <BottomActionBar />

      {FEATURE_FLAG_FINAL_ORDER_VALIDATION && <FinalOrderBackdrop />}
    </div>
  );
};

export default MainPageLayout;
